import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { API_BASE } from '../../config/api';

export const FormBuilder = () => {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('settings'); // settings, fields
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    ux_mode: 'scroll',
    show_progress: true,
    enable_page: true,
    enable_popup: false,
    submit_cta_text: 'Submit',
    success_mode: 'message',
    success_message: 'Thank you! Your submission has been received.',
    success_redirect_url: '',
    default_lead_status: 'new',
    fields: [],
    published: false
  });

  const [selectedFields, setSelectedFields] = useState([]);
  const [fieldSearch, setFieldSearch] = useState('');

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchForms();
    fetchFields();
  }, []);

  const fetchForms = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${backendUrl}/api/admin/forms-builder`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch forms');

      const data = await response.json();
      setForms(data.forms || []);
    } catch (err) {
      console.error(err);
      alert('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const fetchFields = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/form-fields`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setFields(data.fields || []);
      }
    } catch (err) {
      console.error('Failed to fetch fields', err);
    }
  };

  const handleCreate = () => {
    setEditingForm(null);
    setFormData({
      title: '',
      slug: '',
      description: '',
      ux_mode: 'scroll',
      show_progress: true,
      enable_page: true,
      enable_popup: false,
      submit_cta_text: 'Submit',
      success_mode: 'message',
      success_message: 'Thank you! Your submission has been received.',
      success_redirect_url: '',
      default_lead_status: 'new',
      fields: [],
      published: false
    });
    setSelectedFields([]);
    setActiveTab('settings');
    setShowModal(true);
  };

  const handleEdit = (form) => {
    setEditingForm(form);
    setFormData({
      title: form.title || '',
      slug: form.slug || '',
      description: form.description || '',
      ux_mode: form.ux_mode || 'scroll',
      show_progress: form.show_progress !== undefined ? form.show_progress : true,
      enable_page: form.enable_page !== undefined ? form.enable_page : true,
      enable_popup: form.enable_popup || false,
      submit_cta_text: form.submit_cta_text || 'Submit',
      success_mode: form.success_mode || 'message',
      success_message: form.success_message || 'Thank you! Your submission has been received.',
      success_redirect_url: form.success_redirect_url || '',
      default_lead_status: form.default_lead_status || 'new',
      fields: form.fields || [],
      published: form.published || false
    });
    
    // Build selectedFields from form.fields
    const selected = (form.fields || []).map(f => {
      const field = fields.find(field => field.id === f.field_id);
      return {
        ...f,
        fieldData: field
      };
    }).filter(f => f.fieldData);
    
    setSelectedFields(selected);
    setActiveTab('settings');
    setShowModal(true);
  };

  const handleSave = async () => {
    // Validation
    if (!formData.title.trim() || !formData.slug.trim()) {
      alert('Title and Slug are required');
      return;
    }

    if (selectedFields.length === 0) {
      alert('Please add at least one field to the form');
      return;
    }

    // Build fields array
    const fieldsArray = selectedFields.map((f, idx) => ({
      field_id: f.field_id || f.fieldData.id,
      order: idx,
      step_group_id: f.step_group_id || null
    }));

    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const url = editingForm
        ? `${backendUrl}/api/admin/forms-builder/${editingForm.id}`
        : `${backendUrl}/api/admin/forms-builder`;

      const method = editingForm ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          fields: fieldsArray
        })
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save form');
      }

      alert(editingForm ? 'Form updated successfully' : 'Form created successfully');
      setShowModal(false);
      fetchForms();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (form) => {
    if (!window.confirm(`Are you sure you want to delete form "${form.title}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/forms-builder/${form.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to delete form');
      }

      alert('Form deleted successfully');
      fetchForms();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleAddField = (field) => {
    setSelectedFields([...selectedFields, {
      field_id: field.id,
      order: selectedFields.length,
      step_group_id: null,
      fieldData: field
    }]);
    setFieldSearch('');
  };

  const handleRemoveField = (index) => {
    setSelectedFields(selectedFields.filter((_, i) => i !== index));
  };

  const handleMoveField = (index, direction) => {
    const newFields = [...selectedFields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newFields.length) return;
    
    [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
    setSelectedFields(newFields);
  };

  const handleStepGroupChange = (index, stepGroup) => {
    const newFields = [...selectedFields];
    newFields[index].step_group_id = stepGroup ? parseInt(stepGroup) : null;
    setSelectedFields(newFields);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const filteredFields = fields.filter(f => 
    f.label.toLowerCase().includes(fieldSearch.toLowerCase()) ||
    f.key.toLowerCase().includes(fieldSearch.toLowerCase())
  );

  const alreadyAddedFieldIds = selectedFields.map(f => f.field_id || f.fieldData?.id);

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#111827]">Form Builder</h1>
            <p className="text-sm text-[#6B7280] mt-1">Create and manage dynamic forms</p>
          </div>
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-semibold rounded-lg transition-colors"
          >
            + Create Form
          </button>
        </div>

        {/* Forms List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-3 border-[#1E40AF] border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-2 text-sm text-[#6B7280]">Loading forms...</p>
          </div>
        ) : forms.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border-2 border-[#E5E7EB]">
            <svg className="w-16 h-16 mx-auto text-[#9CA3AF] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-semibold text-[#111827] mb-2">No Forms Yet</h3>
            <p className="text-sm text-[#6B7280] mb-4">Create your first form to start collecting submissions</p>
            <button
              onClick={handleCreate}
              className="px-4 py-2 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-semibold rounded-lg transition-colors"
            >
              + Create Form
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {forms.map(form => (
              <div key={form.id} className="bg-white rounded-lg border-2 border-[#E5E7EB] p-6 hover:border-[#1E40AF] transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-[#111827] mb-1">{form.title}</h3>
                    <p className="text-xs text-[#6B7280] font-mono">/forms/{form.slug}</p>
                  </div>
                  {form.published && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                      Published
                    </span>
                  )}
                </div>

                {form.description && (
                  <p className="text-sm text-[#6B7280] mb-4 line-clamp-2">{form.description}</p>
                )}

                <div className="flex items-center gap-2 mb-4 text-xs text-[#6B7280]">
                  <span className="px-2 py-1 bg-[#F3F4F6] rounded font-medium">
                    {form.ux_mode === 'guided' ? '📋 Guided' : '📄 Scroll'}
                  </span>
                  <span className="px-2 py-1 bg-[#F3F4F6] rounded font-medium">
                    {form.fields?.length || 0} fields
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {form.published && form.enable_page && (
                    <a
                      href={`/forms/${form.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 px-3 py-2 bg-[#10B981] hover:bg-[#059669] text-white text-sm font-medium rounded-lg transition-colors text-center"
                    >
                      View
                    </a>
                  )}
                  <button
                    onClick={() => handleEdit(form)}
                    className="flex-1 px-3 py-2 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(form)}
                    className="px-3 py-2 border-2 border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
              <div className="sticky top-0 bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-[#111827]">
                  {editingForm ? 'Edit Form' : 'Create New Form'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F3F4F6] transition-colors"
                >
                  <svg className="w-5 h-5 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Tabs */}
              <div className="border-b border-[#E5E7EB] px-6">
                <div className="flex gap-4">
                  <button
                    onClick={() => setActiveTab('settings')}
                    className={`px-4 py-3 font-semibold text-sm border-b-2 transition-colors ${
                      activeTab === 'settings'
                        ? 'border-[#1E40AF] text-[#1E40AF]'
                        : 'border-transparent text-[#6B7280] hover:text-[#111827]'
                    }`}
                  >
                    Form Settings
                  </button>
                  <button
                    onClick={() => setActiveTab('fields')}
                    className={`px-4 py-3 font-semibold text-sm border-b-2 transition-colors ${
                      activeTab === 'fields'
                        ? 'border-[#1E40AF] text-[#1E40AF]'
                        : 'border-transparent text-[#6B7280] hover:text-[#111827]'
                    }`}
                  >
                    Fields ({selectedFields.length})
                  </button>
                </div>
              </div>

              <div className="p-6">
                {/* Settings Tab */}
                {activeTab === 'settings' && (
                  <div className="space-y-4">
                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-[#111827] mb-2">Form Title *</label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => {
                            setFormData({ 
                              ...formData, 
                              title: e.target.value,
                              slug: editingForm ? formData.slug : generateSlug(e.target.value)
                            });
                          }}
                          placeholder="e.g., Contact Us, Application Form"
                          className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#111827] mb-2">
                          Slug * <span className="text-xs font-normal text-[#6B7280]">(URL: /forms/slug)</span>
                        </label>
                        <input
                          type="text"
                          value={formData.slug}
                          onChange={(e) => setFormData({ ...formData, slug: generateSlug(e.target.value) })}
                          placeholder="e.g., contact-us, application"
                          className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] font-mono text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#111827] mb-2">Description</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Brief description shown above the form"
                        rows={2}
                        className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                      />
                    </div>

                    {/* UX Mode */}
                    <div className="border-2 border-[#E5E7EB] rounded-lg p-4 bg-[#F9FAFB]">
                      <h3 className="text-sm font-bold text-[#111827] mb-3">User Experience</h3>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-semibold text-[#111827] mb-2">UX Mode</label>
                          <select
                            value={formData.ux_mode}
                            onChange={(e) => setFormData({ ...formData, ux_mode: e.target.value })}
                            className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg bg-white focus:outline-none focus:border-[#1E40AF]"
                          >
                            <option value="scroll">Normal (Scroll)</option>
                            <option value="guided">Guided (Step-by-Step)</option>
                          </select>
                        </div>

                        {formData.ux_mode === 'guided' && (
                          <div className="flex items-end">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.show_progress}
                                onChange={(e) => setFormData({ ...formData, show_progress: e.target.checked })}
                                className="w-4 h-4 text-[#1E40AF] rounded"
                              />
                              <span className="text-sm font-semibold text-[#111827]">Show Progress Bar</span>
                            </label>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.enable_page}
                            onChange={(e) => setFormData({ ...formData, enable_page: e.target.checked })}
                            className="w-4 h-4 text-[#1E40AF] rounded"
                          />
                          <span className="text-sm font-medium text-[#111827]">Enable Page (/forms/slug)</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.enable_popup}
                            onChange={(e) => setFormData({ ...formData, enable_popup: e.target.checked })}
                            className="w-4 h-4 text-[#1E40AF] rounded"
                          />
                          <span className="text-sm font-medium text-[#111827]">Enable Popup/Modal</span>
                        </label>
                      </div>
                    </div>

                    {/* Submission Settings */}
                    <div className="border-2 border-[#E5E7EB] rounded-lg p-4 bg-[#F9FAFB]">
                      <h3 className="text-sm font-bold text-[#111827] mb-3">Submission Settings</h3>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-semibold text-[#111827] mb-2">Submit Button Text</label>
                          <input
                            type="text"
                            value={formData.submit_cta_text}
                            onChange={(e) => setFormData({ ...formData, submit_cta_text: e.target.value })}
                            className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg bg-white focus:outline-none focus:border-[#1E40AF]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-[#111827] mb-2">Default Lead Status</label>
                          <select
                            value={formData.default_lead_status}
                            onChange={(e) => setFormData({ ...formData, default_lead_status: e.target.value })}
                            className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg bg-white focus:outline-none focus:border-[#1E40AF]"
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="closed">Closed</option>
                          </select>
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-semibold text-[#111827] mb-2">Success Behavior</label>
                        <select
                          value={formData.success_mode}
                          onChange={(e) => setFormData({ ...formData, success_mode: e.target.value })}
                          className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg bg-white focus:outline-none focus:border-[#1E40AF]"
                        >
                          <option value="message">Show Success Message</option>
                          <option value="redirect">Redirect to URL</option>
                          <option value="both">Show Message + Redirect Button</option>
                        </select>
                      </div>

                      {(formData.success_mode === 'message' || formData.success_mode === 'both') && (
                        <div className="mb-4">
                          <label className="block text-sm font-semibold text-[#111827] mb-2">Success Message</label>
                          <textarea
                            value={formData.success_message}
                            onChange={(e) => setFormData({ ...formData, success_message: e.target.value })}
                            rows={2}
                            className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg bg-white focus:outline-none focus:border-[#1E40AF]"
                          />
                        </div>
                      )}

                      {(formData.success_mode === 'redirect' || formData.success_mode === 'both') && (
                        <div>
                          <label className="block text-sm font-semibold text-[#111827] mb-2">
                            Redirect URL {formData.success_mode === 'both' ? '(Button Target)' : ''}
                          </label>
                          <input
                            type="url"
                            value={formData.success_redirect_url}
                            onChange={(e) => setFormData({ ...formData, success_redirect_url: e.target.value })}
                            placeholder="https://example.com/thank-you"
                            className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg bg-white focus:outline-none focus:border-[#1E40AF]"
                          />
                        </div>
                      )}
                    </div>

                    {/* Publish */}
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.published}
                          onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                          className="w-4 h-4 text-[#1E40AF] rounded"
                        />
                        <span className="text-sm font-semibold text-[#111827]">Published (Visible to Public)</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Fields Tab */}
                {activeTab === 'fields' && (
                  <div className="grid grid-cols-2 gap-6">
                    {/* Available Fields */}
                    <div>
                      <h3 className="text-sm font-bold text-[#111827] mb-3">Available Fields</h3>
                      
                      <div className="mb-3">
                        <input
                          type="text"
                          value={fieldSearch}
                          onChange={(e) => setFieldSearch(e.target.value)}
                          placeholder="Search fields..."
                          className="w-full px-3 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] text-sm"
                        />
                      </div>

                      <div className="border-2 border-[#E5E7EB] rounded-lg max-h-[400px] overflow-y-auto">
                        {filteredFields.length === 0 ? (
                          <div className="text-center py-8 text-sm text-[#6B7280]">
                            {fields.length === 0 ? 'No fields in registry. Create fields first.' : 'No fields match your search.'}
                          </div>
                        ) : (
                          filteredFields.map(field => (
                            <button
                              key={field.id}
                              onClick={() => handleAddField(field)}
                              disabled={alreadyAddedFieldIds.includes(field.id)}
                              className={`w-full px-4 py-3 text-left border-b border-[#E5E7EB] last:border-0 transition-colors ${
                                alreadyAddedFieldIds.includes(field.id)
                                  ? 'bg-[#F3F4F6] text-[#9CA3AF] cursor-not-allowed'
                                  : 'hover:bg-[#F9FAFB] cursor-pointer'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="text-sm font-semibold text-[#111827]">{field.label}</div>
                                  <div className="text-xs text-[#6B7280] font-mono">{field.key}</div>
                                </div>
                                <span className="px-2 py-1 text-xs font-medium bg-[#EEF2FF] text-[#4F46E5] rounded">
                                  {field.field_type}
                                </span>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Selected Fields */}
                    <div>
                      <h3 className="text-sm font-bold text-[#111827] mb-3">Form Fields ({selectedFields.length})</h3>
                      
                      {selectedFields.length === 0 ? (
                        <div className="border-2 border-dashed border-[#E5E7EB] rounded-lg p-8 text-center">
                          <svg className="w-12 h-12 mx-auto text-[#9CA3AF] mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                          </svg>
                          <p className="text-sm text-[#6B7280]">Click fields from the left to add them</p>
                        </div>
                      ) : (
                        <div className="border-2 border-[#E5E7EB] rounded-lg max-h-[400px] overflow-y-auto">
                          {selectedFields.map((field, index) => (
                            <div key={index} className="px-4 py-3 border-b border-[#E5E7EB] last:border-0">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <div className="text-sm font-semibold text-[#111827]">{field.fieldData.label}</div>
                                  <div className="text-xs text-[#6B7280] font-mono">{field.fieldData.key}</div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => handleMoveField(index, 'up')}
                                    disabled={index === 0}
                                    className="p-1 text-[#6B7280] hover:text-[#111827] disabled:opacity-30 disabled:cursor-not-allowed"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleMoveField(index, 'down')}
                                    disabled={index === selectedFields.length - 1}
                                    className="p-1 text-[#6B7280] hover:text-[#111827] disabled:opacity-30 disabled:cursor-not-allowed"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleRemoveField(index)}
                                    className="p-1 text-[#DC2626] hover:text-[#b91c1c]"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              </div>

                              {formData.ux_mode === 'guided' && (
                                <div className="mt-2">
                                  <label className="block text-xs font-semibold text-[#6B7280] mb-1">Step Group</label>
                                  <input
                                    type="number"
                                    min="1"
                                    value={field.step_group_id || ''}
                                    onChange={(e) => handleStepGroupChange(index, e.target.value)}
                                    placeholder="Step # (e.g., 1, 2, 3)"
                                    className="w-full px-2 py-1 border border-[#E5E7EB] rounded text-xs focus:outline-none focus:border-[#1E40AF]"
                                  />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 bg-white border-t border-[#E5E7EB] px-6 py-4 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-white border-2 border-[#E5E7EB] text-[#6B7280] font-semibold rounded-lg hover:bg-[#F3F4F6] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : (editingForm ? 'Update Form' : 'Create Form')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default FormBuilder;
